import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { ModalClose } from "../../svgs/Svgs"
import '../ModalStyle.scss'
import { useParams } from 'react-router-dom'
import DetailsForm from './DetailsForm'
import PayloadForm from './PayloadForm';
import MissionService from '../../../../service/MissionService'
import dayjs from 'dayjs'
import ConfirmationDialog from '../../../CommonComponents/confirmation_dialog/ConfirmationDialog'
import LoaderAnimation from '../Loader/LoaderAnimation'
import { DEFAULT_ORBIT_COLOR } from '../../../../utils/constants'
import { isIPValid, showToastMessage } from '../../../../utils/commonFunctions'
import { getDefaultHWILConfig } from '../../utils'
import { ScenarioResponseType } from '../../types'
import { createQuickDeployScenarioPoller, QUICK_DEPLOY_RETURN_TO_LIST_ERROR } from './quickDeployPolling'

export type SatelliteOrbit = {
    altitude: number;
    eccentricity: number;
    inclination: number;
    RAAN: number;
    AP: number;
    TA: number;
    epoch: number;
}

export type SelectedSatelliteType = {
    label: string,
    value: {
        color: string;
        draftMode: boolean;
        groupId: string;
        groupType: string;
        groupName: string;
        phasingAngle: number;
        phasingType: string;
        satCount: number;
        orbit_data: SatelliteOrbit;
        payloads: PayloadType[]
    }
}

export type SelectedPayloadType = {
    id: string;
    appId: number;
    name: string;
    isConfigured: string;
    payloadType: string;
    payloadSubType: string;
    payloadHWName: string;
    gpio?: { port: string; pin: string[]; interrupt: string }[];
    uartPath0?: string;
    uartPath1?: string;
    interfaces: string[];
    payloadApplicationIP?: string;
    payloadDeviceIP?: string;
    canPath0?: string;
    canPath1?: string;
}
export type PayloadType = {
    label: string;
    value: SelectedPayloadType;
}

export type CreateScenarioType = {
    scenarioName: string;
    scenarioOperation: string;
    scenarioDeploymentType: string;
    sampleRate: number;
    scenarioStartTime: number;
    hwInfo: {
        uniqueId: string;
        name: string;
        category: string;
    }[]
    remoteAppConfiguration: {
        RemoteApp: {
            AppData: {
                id: string;
                appId: number;
                name: string;
                isConfigured: string;
                payloadType: string;
                payloadSubType: string;
                payloadHWName: string;
                gpio?: { port: string; pin: string[]; interrupt: string }[];
                uartPath0?: string;
                uartPath1?: string;
                payloadApplicationIP?: string;
                payloadDeviceIP?: string;
                canPath0?: string;
                canPath1?: string;
            }[];
        }
    }
}

export type SatelliteType = {
    satelliteGroups: {
        color: string;
        phasingAngle: number;
        phasingType: string;
        groupId: string;
        referenceOrbit: SatelliteOrbit;
    }[];
}


export type DeployRemotePayloadScenario = {
    remotePayloadName: string;
    scenarioInfo: CreateScenarioType;
    addSatelliteGroups: SatelliteType;

}


type Props = {
    open: boolean,
    close: () => void
}

export type GpioPinType = {
    port: string;
    pin: string[];
    interrupt: string;
};

export type GpioPinsType = [GpioPinType, GpioPinType, GpioPinType, GpioPinType];

const QuickDeployModal: FC<Props> = ({ open, close, }) => {
    const { mission_id } = useParams()
    const [step, set_step] = useState<string>('Details')
    const [all_satellites, set_all_satellites] = useState<SelectedSatelliteType[]>([])
    const [scenarioTitle, setScenarioTitle] = useState<string>('')
    const [scenario, setScenario] = useState<ScenarioResponseType | null>(null)
    const [selectedGroup, setSelectedGroup] = useState<SelectedSatelliteType | null>(null)
    const [selectedPayload, setSelectedPayload] = useState<SelectedPayloadType | null>(null)
    const [loadingState, setLoadingState] = useState<string | boolean>(false)
    const [openConfirmationDialouge, setOpenConfirmationDialouge] = useState(false)
    const isMountedRef = useRef(true)
    const abortControllerRef = useRef<AbortController | null>(null)
    const quickDeployPoller = useMemo(() => createQuickDeployScenarioPoller(), [])

    const scenario_start_time = useMemo(() => dayjs.utc(new Date()).unix(), [])
    const needPayloadconfiguration = useMemo(() => selectedPayload && selectedPayload?.interfaces.some(port => ['GPIO', 'CAN', "UART", 'Ethernet'].includes(port)), [selectedPayload])
    const disabled_navigation = useMemo(() => (!scenarioTitle || !selectedGroup) ? ['Bus', 'Payload'] : ['Bus'], [scenarioTitle, selectedGroup])

    const [scenarioDescription, setScenarioDescription] = useState<string>('')
    const [errorMessage, setErrorMessage] = useState<string>('Error occured')

    const disabled_btn = useMemo(() => {
        if (step === "Details") {
            return !scenarioTitle || !selectedGroup || !selectedPayload;
        }
        if (step === "Payload") {
            const Interfaces = selectedPayload && selectedPayload?.interfaces || []
            const disabled = Interfaces?.length === 0 || (Interfaces.includes('CAN') && selectedPayload?.canPath0 === '') || (Interfaces.includes('Ethernet') && !isIPValid(selectedPayload?.payloadDeviceIP ?? ''))
            return disabled;
        }
        return false;
    }, [step, scenarioTitle, selectedGroup, selectedPayload]);

    const getSatelliteGroups = async () => {
        // Cancel any pending request
        abortControllerRef.current?.abort()

        // Create new AbortController for this request
        const abortController = new AbortController()
        abortControllerRef.current = abortController

        try {
            const response = await MissionService.getMissionSatInfo(mission_id)

            // Check if request was cancelled or component unmounted
            if (abortController.signal.aborted || !isMountedRef.current) return

            const groups = (response?.data?.satelliteGroup || []).filter(group => (group?.satelliteGroup?.satCount === 1 && group?.satelliteGroup?.draftMode === false))

            const all_groups_options: any = []
            groups.map((group: any) => {
                const group_payloads = group?.['payloads'] || []
                const updated_payloads = group_payloads.map((payload: any) => ({
                    ...payload,
                    ...group?.satelliteGroup?.satConfig?.payload?.find(match_payload => payload.uniqueId === match_payload?.payloadId)
                }))
                const all_payload_options = updated_payloads.map((payload: any) => {
                    const details = {
                        id: payload?.uniqueId as string,
                        appId: Number(payload?.appId) as number,
                        name: payload?.name as string,
                        isConfigured: "Yes" as "Yes",
                        payloadType: payload?.["atmosData"]?.["component_specific_attributes"]?.["payloadType"] as string,
                        payloadSubType: payload?.["atmosData"]?.["component_specific_attributes"]?.["payloadSubType"] as string,
                        payloadHWName: payload?.["atmosData"]?.['name'] as string,
                        interfaces: payload?.["userData"]?.["selected_interfaces"] as string[],
                    }
                    return {
                        label: payload?.['name'],
                        value: {
                            ...details,
                            ...getDefaultHWILConfig(JSON.stringify(details))
                        }
                    }
                })
                all_groups_options.push({
                    label: group?.satelliteGroup?.["name"],
                    value: {
                        color: JSON.stringify(DEFAULT_ORBIT_COLOR),
                        groupId: group?.satelliteGroup?.['uniqueId'],
                        groupType: group?.satelliteGroup?.['groupType'],
                        groupName: group?.satelliteGroup?.["name"],
                        satCount: group?.satelliteGroup?.["satCount"],
                        phasingAngle: group?.satelliteGroup?.['phasingAngle'],
                        phasingType: group?.satelliteGroup?.["phasingType"],
                        draftMode: group?.satelliteGroup?.["draftMode"],
                        orbit_data: {
                            altitude: group?.satelliteGroup?.['referenceOrbitParameters']?.['altitude'],
                            inclination: group?.satelliteGroup?.['referenceOrbitParameters']?.['inclination'],
                            eccentricity: group?.satelliteGroup?.['referenceOrbitParameters']?.['eccentricity'],
                            RAAN: group?.satelliteGroup?.['referenceOrbitParameters']?.['RAAN'],
                            AP: group?.satelliteGroup?.['referenceOrbitParameters']?.['AP'],
                            TA: group?.satelliteGroup?.['referenceOrbitParameters']?.['TA'],
                            epoch: scenario_start_time //need change here
                        },
                        payloads: all_payload_options || []
                    }
                });
            })

            // Only update state if component is still mounted and request wasn't cancelled
            if (isMountedRef.current && !abortController.signal.aborted) {
                set_all_satellites(all_groups_options)
            }
        } catch (error: any) {
            // Don't show error if request was cancelled or component unmounted
            if (abortController.signal.aborted || !isMountedRef.current) return

            // Only show error for actual failures, not cancellations
            if (error?.name !== 'AbortError' && error?.name !== 'CanceledError') {
                console.log(error);
                showToastMessage('error', "Something went wrong while fetching satellite groups")
            }
        } finally {
            // Clear abort controller if this was the current request
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null
            }
        }
    }

    useEffect(() => {
        isMountedRef.current = true
        if (!mission_id) return
        getSatelliteGroups()

        return () => {
            isMountedRef.current = false
            abortControllerRef.current?.abort()
            abortControllerRef.current = null
            quickDeployPoller.cancel()
        }
    }, [mission_id])

    const onClose = () => {
        if (scenarioTitle || selectedGroup || loadingState === "download") {
            setOpenConfirmationDialouge(true)
        } else {
            onReset()
        }
    }

    const onReset = () => {
        set_step('Details')
        setScenarioTitle('')
        setScenarioDescription('')
        setSelectedGroup(null)
        setSelectedPayload(null)
        setLoadingState(false)
        setOpenConfirmationDialouge(false)
        quickDeployPoller.cancel()
        close()
    }

    const set_deployment_step_forward = () => {
        switch (step) {
            case 'Details':
                set_step('Payload')
                break;
            default:
                set_step('Details')
                break;
        }
    }

    const set_deployment_step_backward = () => {
        switch (step) {
            case 'Payload':
                set_step('Details')
                break;
            default:
                set_step('Details')
                break;
        }
    }

    const deploy = async () => {
        /* this function consist of the api which will handle the overall deployment process 
        starting from Creating Scenario -> Patch Satellite -> Post Grafana */
        if (!mission_id || !selectedGroup || !selectedPayload) return;
        setLoadingState('deploying')
        const { interfaces: _interfaces, ...payloadWithoutInterface } = selectedPayload as SelectedPayloadType;
        const hwilScenarioDeploymentFromData: DeployRemotePayloadScenario = {
            remotePayloadName: selectedPayload?.name,
            scenarioInfo: {
                scenarioName: scenarioTitle,
                scenarioOperation: 'CONOPS',
                scenarioDeploymentType: "QUICK_DEPLOY",
                sampleRate: 1,
                scenarioStartTime: scenario_start_time,
                hwInfo: [{
                    uniqueId: selectedPayload.id,
                    name: selectedPayload.name,
                    category: 'Payload'
                }],
                remoteAppConfiguration: {
                    RemoteApp: {
                        AppData: [payloadWithoutInterface]
                    }
                }
            },
            addSatelliteGroups: {
                satelliteGroups: [
                    {
                        color: selectedGroup.value.color,
                        phasingAngle: selectedGroup.value.phasingAngle,
                        phasingType: selectedGroup.value.phasingType,
                        groupId: selectedGroup.value.groupId,
                        referenceOrbit: selectedGroup.value.orbit_data
                    }
                ]
            },

        }

        try {
            const response = await MissionService.hwilScenarioDeployment(mission_id, hwilScenarioDeploymentFromData)
            const scenarioData = response?.data?.data?.[0] || null
            const runningScenario = await quickDeployPoller.poll({
                missionId: mission_id,
                scenarioTitle,
                initialScenario: scenarioData
            })
            if (!isMountedRef.current || !runningScenario) return
            setScenario(runningScenario)
            setLoadingState('download')
        } catch (error: any) {
            if (!isMountedRef.current) return
            const message = (typeof error?.response?.data?.message === 'string' && !error?.response?.data?.message.includes('html'))
                ? error?.response?.data?.message
                : error?.message || 'Something went wrong, deployment failed'

            if (error?.name === QUICK_DEPLOY_RETURN_TO_LIST_ERROR) {
                showToastMessage('error', message)
                onReset()
                return
            }

            setLoadingState("error")
            setErrorMessage(message)
            showToastMessage('error', message);
        } finally {
            quickDeployPoller.cancel()
        }
    };

    return (
        <div className={`deploy__model ${open && 'active'}`}>
            <div className="deploy__model__center large">
                {!loadingState &&
                    <Fragment>
                        <div className="deploy__model__center__header">
                            <span className="fs-20">Quick Deploy</span>
                            <button className="header__close__button"
                                onClick={onClose}>
                                <ModalClose />
                            </button>
                        </div>

                        <div className='deploy__model__center__body'>
                            <div className='model__center__body__container'>
                                {step === 'Details' &&
                                    <DetailsForm
                                        selectedGroup={selectedGroup}
                                        scenarioTitle={scenarioTitle}
                                        all_satellites={all_satellites}
                                        setScenarioTitle={setScenarioTitle}
                                        setSelectedGroup={(group) => {
                                            setSelectedGroup(group)
                                        }}
                                        all_payloads={selectedGroup?.value?.payloads as PayloadType[]}
                                        selectedPayload={selectedPayload}
                                        setSelectedPayload={setSelectedPayload}
                                        scenarioDescription={scenarioDescription}
                                        setScenarioDescription={setScenarioDescription}
                                    />
                                }
                                {(step === 'Payload' && selectedPayload && needPayloadconfiguration) &&
                                    <PayloadForm
                                        selectedPayload={selectedPayload}
                                        setSelectedPayload={setSelectedPayload}
                                    />
                                }

                            </div>

                        </div>

                        <div className="deploy__model__center__footer">
                            <button className="footer__close__button"
                                onClick={() => {
                                    step === 'Details' ? onClose() : set_deployment_step_backward()
                                }}>
                                {step === 'Details' ? 'Close' : 'Back'}
                            </button>
                            <button className="footer__next__button"
                                disabled={disabled_btn}
                                onClick={() => {
                                    if (step === 'Payload' || (step === 'Details' && selectedPayload && !needPayloadconfiguration)) {
                                        deploy()
                                    } else {
                                        set_deployment_step_forward();
                                    }
                                }}>
                                {(step === 'Payload' || (step === 'Details' && selectedPayload && !needPayloadconfiguration))
                                    ? "Deploy"
                                    : "Next"}
                            </button>
                        </div>
                    </Fragment>
                }

                {loadingState &&
                    <LoaderAnimation
                        loadingState={loadingState}
                        onClose={onClose}
                        scenario={scenario}
                        setLoadingState={setLoadingState}
                        errorMessage={errorMessage}
                    />
                }
            </div>
            <ConfirmationDialog
                open={openConfirmationDialouge}
                type={"delete"}
                title={"Discard Deployment"}
                description={`Discarding will take you back to the list of deployments`}
                onConfirm={() => {
                    onReset()
                }}
                onCancel={() => {
                    setOpenConfirmationDialouge(false)
                }}
                confirm_button_text="Discard"
            />

        </div>
    )
}

export default QuickDeployModal
